import React from 'react';
import { motion } from 'framer-motion';
import { Gift, Heart, Sparkles } from 'lucide-react';

// Thin top banner version - solid, non-transparent, acts as page border
export function RamadanTopBanner() {
    return (
        <div className="w-full bg-secondary text-secondary-foreground py-2 px-4">
            <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm">
                <Sparkles className="w-4 h-4" />
                <span className="font-medium">Eid Al-Fitr Special:</span>
                <span>Use code</span>
                <span className="font-bold bg-secondary-foreground/20 px-2 py-0.5 rounded">EIDALFITR1447</span>
                <span>at checkout</span>
                <Sparkles className="w-4 h-4" />
            </div>
        </div>
    );
}

// Large promotional badge - for placement below discount code input
export function RamadanPromoBadge() {
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="w-full bg-gradient-to-r from-secondary/20 via-secondary/10 to-secondary/20 border-2 border-secondary/50 rounded-2xl p-6 mt-6"
        >
            <div className="flex flex-col items-center text-center gap-4">
                {/* Icon and Title */}
                <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-secondary/20 border border-secondary/30">
                        <Sparkles className="w-6 h-6 text-secondary" />
                    </div>
                    <h3 className="text-xl font-bold text-secondary whitespace-nowrap">
                        Eid Al-Fitr Special Offer
                    </h3>
                </div>
                
                {/* Code */}
                <p className="text-base text-muted-foreground">
                    Use code <span className="font-bold text-secondary bg-secondary/10 px-2 py-1 rounded">EIDALFITR1447</span> for a discount
                </p>
                
                {/* Charity message - below code */}
                <div className="flex items-center gap-2 bg-primary/10 rounded-xl px-4 py-2 border border-primary/20">
                    <Heart className="w-5 h-5 text-secondary fill-secondary" />
                    <span className="text-sm font-semibold text-primary whitespace-nowrap">Part of proceeds go to charity</span>
                </div>
            </div>
        </motion.div>
    );
}

// Old version kept for backwards compatibility - redirects to new components
export function RamadanPromoBanner({ variant = 'landing' }) {
    // For landing page, use the top banner style
    if (variant === 'landing') {
        return null; // Will be replaced with RamadanTopBanner at page level
    }
    // For checkout, use the badge style
    return <RamadanPromoBadge />;
}

export function CharityInfoBox() {
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-2xl p-6 my-8"
        >
            <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-secondary fill-secondary" />
                </div>
                <div>
                    <h4 className="text-lg font-bold text-foreground mb-2">
                        Chase Ibadah & Give Sadaqah in One
                    </h4>
                    <p className="text-muted-foreground leading-relaxed">
                        When you subscribe to aalim.ai, you're not just investing in your Islamic education — 
                        you're also giving back. <span className="text-primary font-semibold">A portion of every subscription 
                        goes directly to charity</span>, supporting those in need around the world.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-3">
                        <div className="flex items-center gap-2 text-sm text-secondary">
                            <Sparkles className="w-4 h-4" />
                            <span>Seek knowledge</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-secondary">
                            <Heart className="w-4 h-4 text-secondary fill-secondary" />
                            <span>Give sadaqah</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-secondary">
                            <Gift className="w-4 h-4" />
                            <span>Earn multiple rewards</span>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

export function CheckoutCharityMessage() {
    return (
        <div className="bg-gradient-to-r from-secondary/10 via-primary/10 to-secondary/10 border border-primary/20 rounded-xl p-4 mt-4">
            <div className="flex items-center gap-3">
                <Heart className="w-6 h-6 text-secondary fill-secondary flex-shrink-0" />
                <div>
                    <p className="text-sm text-foreground">
                        <span className="font-semibold text-secondary">Your subscription supports charity.</span>
                        {' '}Part of your payment will be donated to help those in need — 
                        earn rewards for seeking knowledge and giving sadaqah at the same time!
                    </p>
                </div>
            </div>
        </div>
    );
}
